import { useMemo, useState } from 'react';

import { Maybe } from 'types/UtilityTypes';

import {
  BatchLTLDraftShipment,
  formatBatchLTLDraftTemplate,
} from '../utils/formatBatchLTLDraftTemplate';

type UseBatchLTLDraftParams = {
  templateBody: string;
  // The rep's edited draft saved before they left the email.
  initialEditedDraft?: Maybe<string>;
  customerName: Maybe<string>;
  shipments: BatchLTLDraftShipment[];
  hideTaiLimitedLiability: boolean;
};

// The generated draft follows every field, rate and selection change until the rep edits
// it; after that the rep's text stays until they reset it.
export function useBatchLTLDraft({
  templateBody,
  initialEditedDraft = null,
  customerName,
  shipments,
  hideTaiLimitedLiability,
}: UseBatchLTLDraftParams) {
  const generatedDraft = useMemo(
    () =>
      formatBatchLTLDraftTemplate(templateBody, {
        customerName,
        shipments,
        hideTaiLimitedLiability,
      }),
    [templateBody, customerName, shipments, hideTaiLimitedLiability]
  );
  const [editedDraft, setEditedDraft] =
    useState<Maybe<string>>(initialEditedDraft);
  const [editingDraft, setEditingDraft] = useState<Maybe<string>>(null);

  const saveRepEdit = (value: string) => {
    setEditingDraft(null);
    // Jodit re-serializes HTML on blur; only text changes count as a rep edit.
    if (getDraftText(value) !== getDraftText(generatedDraft)) {
      setEditedDraft(value);
    }
  };

  return {
    draft: editingDraft ?? editedDraft ?? generatedDraft,
    editedDraft,
    isRepEdited: editingDraft !== null || editedDraft !== null,
    saveRepEdit,
    resetToGenerated: () => {
      setEditingDraft(null);
      setEditedDraft(null);
    },
  };
}

function getDraftText(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;|\s+/g, '');
}
